import { useState } from 'react';
import { Sheet, Button, Field, FIELD_CONTROL, Icon, cn } from './ui';

// Feedback na een wedstrijdavond. Verschijnt eenmalig per wedstrijddatum.
//
// De sterrenrij was een rij losse knoppen met een ★-teken en alleen een
// aria-label; het was voor een schermlezer niet af te lezen wat er gekozen wás.
// Het is nu een radiogroup met aria-checked, zodat de huidige waarde en het
// bereik ("3 van 5") wél worden voorgelezen.
export default function WedstrijdFeedbackModal({ date, onClose }) {
  const [ratingCompetition,  setRatingCompetition]  = useState(0);
  const [ratingOrganization, setRatingOrganization] = useState(0);
  const [comment,            setComment]            = useState('');
  const [submitting,         setSubmitting]         = useState(false);
  const [submitted,          setSubmitted]          = useState(false);

  async function handleSubmit() {
    if (!ratingCompetition || !ratingOrganization) return;
    setSubmitting(true);
    try {
      await fetch('/api/competition-feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date,
          ratingCompetition,
          ratingOrganization,
          comment: comment.trim() || null,
        }),
      });
    } catch {
      // best-effort; still mark as submitted locally
    }
    localStorage.setItem(`feedback_${date}`, '1');
    setSubmitting(false);
    setSubmitted(true);
    setTimeout(onClose, 2200);
  }

  const canSubmit = ratingCompetition > 0 && ratingOrganization > 0;

  return (
    <Sheet
      open
      onOpenChange={open => !open && onClose()}
      title="Wedstrijdfeedback"
      description={submitted ? undefined : 'Hoe vond je de avond? Je reactie helpt ons verbeteren.'}
      footer={
        submitted ? null : (
          <>
            <Button variant="ghost" onClick={onClose}>Overslaan</Button>
            <Button variant="primary" onClick={handleSubmit} disabled={!canSubmit || submitting}>
              {submitting ? 'Versturen…' : 'Verstuur feedback'}
            </Button>
          </>
        )
      }
    >
      {submitted ? (
        <div className="flex flex-col items-center gap-2 py-8">
          <span className="flex h-14 w-14 items-center justify-center rounded-pill border-2 border-green-500 bg-green-100 text-green-600">
            <Icon name="check" size={26} />
          </span>
          <p className="mt-2 font-heading text-[18px] font-bold text-ink">Bedankt voor je feedback!</p>
          <p className="text-center text-[14.5px] text-ink-3">We nemen het mee voor de volgende wedstrijd.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          <RatingRow label="Wedstrijd" value={ratingCompetition} onChange={setRatingCompetition} />
          <RatingRow label="Organisatie" value={ratingOrganization} onChange={setRatingOrganization} />

          <Field label="Opmerking" hint="Optioneel">
            {props => (
              <textarea
                {...props}
                value={comment}
                onChange={e => setComment(e.target.value)}
                placeholder="Wat ging goed, wat kan beter?"
                rows={3}
                className={`${FIELD_CONTROL} resize-y`}
              />
            )}
          </Field>
        </div>
      )}
    </Sheet>
  );
}

function RatingRow({ label, value, onChange }) {
  const [hovered, setHovered] = useState(0);

  return (
    <div className="flex items-center justify-between gap-3">
      <span className="font-heading text-[16px] font-semibold text-ink">{label}</span>
      <div
        role="radiogroup"
        aria-label={`${label}: beoordeling van 1 tot 5`}
        className="flex gap-0.5"
        onMouseLeave={() => setHovered(0)}
      >
        {[1, 2, 3, 4, 5].map(n => {
          const filled = n <= (hovered || value);
          return (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={value === n}
              aria-label={`${n} van 5`}
              onMouseEnter={() => setHovered(n)}
              onClick={() => onChange(n)}
              className={cn(
                'flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border-none bg-transparent',
                'transition-colors duration-100',
                'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary-500',
                filled ? 'text-gold' : 'text-line-strong hover:text-gold',
              )}
            >
              <Icon name="star" size={22} fill />
            </button>
          );
        })}
      </div>
    </div>
  );
}
